import { image } from '~/js/utils/image';
import transitionEnd from '~/js/utils/transition-end';
import Recommendations from '~/js/lib/recommendations';
import { buildProductItems } from '~/js/utils/product-item';
import quickBuyPopupTmpl from '~/js/lib/quick-buy-popup-tmpl';

export function fetchCart() {
  return fetch('/cart?view=data', { cache: 'no-store' }).then((res) => res.json());
}

export async function removeItem(id) {
  const data = new FormData();
  data.append('id', id);
  data.append('quantity', '0');

  return (await fetch('/cart/change.js', {
    method: 'POST',
    body: data,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })).json();
}

/**
 * @param {Array} formData
 * @param {Function} callback
 * @param options
 * @returns
 */
export function addToCart(formData, callback, options) {
  fetch('/cart/add.js', {
    method: 'POST',
    body: formData,
    ...options,
  })
    .then(() => {
      document.dispatchEvent(new CustomEvent('cart:product:added', {
        detail: {
          item: formData.get('id'),
        },
      }));
      callback();
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error:', error);
    });
}

export function updateCartCounts(cart) {
  const counts = document.querySelectorAll('[data-cart-count]');

  counts.forEach((count) => {
    count.textContent = cart.item_count;
  });
}

async function powerstepRelewiseSlider(node, item) {
  const sliderElm = node.querySelector('[data-relewise-slider]');
  const {
    type,
  } = node.dataset;
  const {
    sliderConfig, sectionId, id, label,
  } = sliderElm.dataset;

  const idsArray = item.relewise_id ? [item.relewise_id] : [];

  if (!idsArray.length) return;

  const RecommendationsModule = new Recommendations({ displayedAtLocation: label });
  const jsonSettings = JSON.parse(sliderConfig);
  const popupContainer = node.parentNode.querySelector('[data-popup-container]');
  const itemContainer = node.querySelector('[data-slider]');

  const initSlider = () => {
    const swiperConfig = {
      ...jsonSettings,
      scrollbar: {
        el: `[data-slider-scrollbar="${id}"]`,
        draggable: true,
        dragClass: 'slider-scrollbar__drag',
        horizontalClass: 'slider-scrollbar--horizontal',
      },
    };

    const init = async () => {
      const { default: Swiper } = await import(/* webpackChunkName: "swiper" */ 'modules/swiper');

      const slider = new Swiper(itemContainer, swiperConfig);
      slider.init();

      return slider;
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          init();

          observer.disconnect();
        }
      });
    });

    observer.observe(node);
    return init;
  };

  const setRecoms = async () => {
    let recomsResponse = null;

    switch (type) {
      case 'PurchasedWithCurrentCart':
        recomsResponse = await RecommendationsModule.getPurchasedWithProductRecoms({
          productId: item.relewise_id,
          categoryId: item.look_collections?.[0]?.id,
          vendor: item.vendor,
        });
        break;

      case 'Cart':
        recomsResponse = await RecommendationsModule.getCartUpsells({ productAndVariantIds: idsArray });
        break;

      case 'RecentlyViewedProductsRequest':
        recomsResponse = await RecommendationsModule.getRecentlyViewedProducts();
        break;

      case 'PersonalProductRecommendationRequest':
        recomsResponse = await RecommendationsModule.getPersonalProductRecoms();
        break;

      case 'ProductsViewedAfterViewingProductRequest':
        recomsResponse = await RecommendationsModule.getProductsViewedAfterViewingProduct(idsArray[0]);
        break;

      default:
        recomsResponse = await RecommendationsModule.getPopularProducts();
        break;
    }

    if (!recomsResponse.status || recomsResponse.status === 200) {
      const products = recomsResponse.recommendations;

      if (products.length) {
        itemContainer.firstElementChild.innerHTML = buildProductItems(products, 'card', 'relewise', sectionId);

        const initSliderFn = initSlider();
        initSliderFn().then(() => {
          // Scrollbar is now handled by Swiper's built-in scrollbar
        });

        if (popupContainer) {
          let popups = '';

          const popupProducts = products.filter((p) => p.allVariants.length > 1);

          popupProducts.forEach((product) => {
            popups += quickBuyPopupTmpl(product, 'relewise', sectionId);
          });

          popupContainer.innerHTML = popups;
        }

        document.dispatchEvent(new Event('app:remount'));
      } else {
        node.parentNode.parentNode.hidden = true;
      }
    }
  };

  await setRecoms();
}

async function updateShippingBanner(element) {
  const freshPage = await fetch(window.location.href).then((res) => res.text());
  const freshPageDOM = new DOMParser().parseFromString(freshPage, 'text/html');

  const newBanner = freshPageDOM.querySelector('[data-powerstep-shipping]');

  if (newBanner) {
    element.innerHTML = newBanner.innerHTML;
  }
}

/**
 * Shows the "free gift added" row for gifts added by this add-to-cart. Only ever shows: one add can
 * fire the added event twice, and the second sync finds the gift already there. Hidden on close.
 */
function updatePowerstepGifts(powerstep, gifts) {
  const wrapper = powerstep.querySelector('[data-powerstep-gift]');
  if (!wrapper || !gifts.length) return;

  const list = wrapper.querySelector('[data-powerstep-gift-items]');
  const template = wrapper.querySelector('[data-powerstep-gift-template]');
  list.replaceChildren(...gifts.map((gift) => {
    const node = template.content.cloneNode(true);
    node.querySelector('[data-gift-title]').textContent = gift.title;
    node.querySelector('[data-gift-price]').textContent = Shopify.formatMoney(gift.price, theme.moneyFormat);
    if (gift.image) node.querySelector('[data-gift-image]').innerHTML = image(gift.image, 'mix-blend-darken');
    return node;
  }));
  wrapper.hidden = false;
}

export async function updatePowerstep(item, gifts = []) {
  const powerstep = document.getElementById('powerstep');
  if (!powerstep) return;

  const content = powerstep.querySelector('[data-powerstep-content]');
  const imageWrapper = powerstep.querySelector('[data-powerstep-image]');
  const title = powerstep.querySelector('[data-powerstep-title]');
  const variant = powerstep.querySelector('[data-powerstep-variant]');
  const price = powerstep.querySelector('[data-powerstep-price]');
  const shippingBanner = powerstep.querySelector('[data-powerstep-shipping]');
  const relewiseSliders = powerstep.querySelectorAll('[data-type][data-ids]');

  // Start recommendation loading and await completion before showing content
  const recomPromises = Array.from(relewiseSliders).map((slider) => powerstepRelewiseSlider(slider, item));

  try {
    updatePowerstepGifts(powerstep, gifts);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Free gift row:', error);
  }
  title.textContent = item.title;
  variant.textContent = item.variant_title.toLowerCase() !== 'default title' ? item.variant_title : '';

  await updateShippingBanner(shippingBanner);

  if (recomPromises.length) {
    await Promise.allSettled(recomPromises);
  }

  powerstep.hidden = false;

  setTimeout(() => {
    powerstep.classList.add('!opacity-100');
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      powerstep.classList.remove('opacity-0');
    }, 151);
  }, 1);

  price.innerHTML = `
    <span class="lg:font-semibold">
      ${Shopify.formatMoney(item.price, theme.moneyFormat)}
    </span>
  `;
  imageWrapper.innerHTML = image(item.featured_image, 'mix-blend-darken');

  // Reveal content after everything is ready
  if (content) {
    content.classList.remove('opacity-0');
  }
}

export function initPowerstep() {
  const powerstep = document.getElementById('powerstep');
  if (!powerstep) return;

  const closeButtons = powerstep.querySelectorAll('[data-dismiss]');

  const closePowerstep = () => {
    const giftRow = powerstep.querySelector('[data-powerstep-gift]');
    if (giftRow) giftRow.hidden = true;

    powerstep.classList.remove('!opacity-100');
    powerstep.classList.add('opacity-0');
    document.body.style.overflow = null;

    transitionEnd(powerstep, () => {
      powerstep.hidden = true;
    });
  };

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      closePowerstep();
    });
  });

  document.addEventListener('click', (e) => {
    if (e.target.id === 'powerstep') {
      closePowerstep();
    }
  });
}
